import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BaseChartDirective, provideCharts } from 'ng2-charts';
import {
  CategoryScale,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';

import { NgxScannerQrcodeModule, LOAD_WASM } from 'ngx-scanner-qrcode';
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { MenusComponent } from './menus/menus.component';
import { CompaniesComponent } from './companies/companies.component';
import { CompanyComponent } from './company/company.component';
import { UsersComponent } from './users/users.component';
import { UserComponent } from './user/user.component';
import { WorkersComponent } from './workers/workers.component';
import { WorkerComponent } from './worker/worker.component';
import { ZonesComponent } from './zones/zones.component';
import { ZoneComponent } from './zone/zone.component';
import { ProjectsComponent } from './projects/projects.component';
import { ProjectComponent } from './project/project.component';
import { DevicesComponent } from './devices/devices.component';
import { DeviceComponent } from './device/device.component';
import { ConfigurationComponent } from './configuration/configuration.component';
import { DialogComponent } from './dialog/dialog.component';
import { HomeComponent } from './home/home.component';
import { ReportComponent } from './report/report.component';
import { DashboardOverviewComponent } from './dashboard-overview/dashboard-overview.component';
import { DeviceOperationsComponent } from './device-operations/device-operations.component';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSort, MatSortModule }  from '@angular/material/sort';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { CompanyService } from './services/company.service';
import { UserService } from './services/user.service';
import { ProjectService } from './services/project.service';
import { DeviceService } from './services/device.service';
import { ConfigurationService } from './services/configuration.service';
import { LoginService } from './services/login.service';
import { AuthService } from './services/auth.service';
import { StorageService } from './services/storage.service';
import { LanguageService } from './services/language.service';
import { WorkerService } from './services/worker.service';

import { HttpClientModule } from '@angular/common/http';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { environment } from '../environments/environment';
import { mockInterceptor } from './interceptors/mock.interceptor';
import { headersInterceptor } from './interceptors/headers.interceptor';

import { TranslocoRootModule } from './transloco/transloco-root.module';
import { TranslocoRootModule as TranslocoRootModule_alias } from './transloco/transloco-root.module';
import { FaqComponent } from './faq/faq.component';
import { ImportComponent } from './import/import.component';
import { DeviceScannerComponent } from './device-scanner/device-scanner.component';
import { DeviceStatusPipe } from './pipes/device-status.pipe';
import { CompanyDatePipe } from './pipes/company-date.pipe';
import { AlarmConfigurationComponent } from './alarm-configuration/alarm-configuration.component';
import { QrDeviceRedirectComponent } from './qr-device/qr-device-redirect.component';


@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    MenusComponent,
    CompaniesComponent,
    CompanyComponent,
    UsersComponent,
    UserComponent,
    ZonesComponent,
    ZoneComponent,
    WorkersComponent,
    WorkerComponent,
    ProjectsComponent,
    ProjectComponent,
    DevicesComponent,
    DeviceComponent,
    ConfigurationComponent,
    DialogComponent,
    HomeComponent,
    DashboardOverviewComponent,
    DeviceOperationsComponent,
    FaqComponent,
    ImportComponent,
    DeviceScannerComponent,
    ReportComponent,
    DeviceStatusPipe,
    CompanyDatePipe,
    AlarmConfigurationComponent,
    QrDeviceRedirectComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatToolbarModule,
    MatInputModule,
    MatCardModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSidenavModule,
    MatButtonToggleModule,
    MatDividerModule,
    MatCheckboxModule,
    MatSort,
    MatSortModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatTabsModule,
    MatProgressBarModule,
    HttpClientModule,
    BaseChartDirective,
    TranslocoRootModule,
    TranslocoRootModule_alias, 
    NgxScannerQrcodeModule,
  ],
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    CompanyService,
    UserService,
    ProjectService,
    DeviceService,
    ConfigurationService,
    LoginService,
    AuthService,
    StorageService,
    LanguageService,
    WorkerService,
    TranslocoRootModule,
    provideHttpClient(withInterceptors(
      environment.useMock ? [mockInterceptor] : [headersInterceptor]
    )),
    provideAnimationsAsync(),
    provideCharts({
      registerables: [
        LineController,
        LineElement,
        PointElement,
        CategoryScale,
        LinearScale,
        Tooltip,
        Legend,
      ],
    })
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
